import { useCallback, useEffect, useState } from "react";
import { isAuthenticated } from "../apicalls/auth";
import {
  getAdminDemoPolicies,
  upsertAdminDemoPolicy,
  updateAdminDemoPolicy,
} from "../apicalls/adminDemos";
import "./style/AdminDemos.css";

export function AdminDemoPolicies() {
  const token = isAuthenticated()?.access_token;
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editPolicy, setEditPolicy] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newVendorId, setNewVendorId] = useState("");

  const fetchPolicies = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminDemoPolicies(token);
      if (data && Array.isArray(data.policies)) {
        setPolicies(data.policies);
      } else {
        setError("Failed to load policies");
      }
    } catch (err) {
      setError(err.message || "Failed to load policies");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const handleUpsert = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData(e.target);
      const payload = {
        vendor_id: Number(newVendorId),
        base_fee_cents: Number(fd.get("base_fee_cents")),
        base_fee_tax_cents: Number(fd.get("base_fee_tax_cents")),
        covered_days: Number(fd.get("covered_days")),
        late_fee_cents: Number(fd.get("late_fee_cents")),
        late_fee_cap_cents: Number(fd.get("late_fee_cap_cents")),
        conversion_day: Number(fd.get("conversion_day")),
        max_rackets_per_ticket: Number(fd.get("max_rackets_per_ticket")),
        release_before_payment: fd.get("release_before_payment") === "true",
        sales_tax_bps: fd.get("sales_tax_bps") ? Number(fd.get("sales_tax_bps")) : null,
      };
      const result = await upsertAdminDemoPolicy(payload, token);
      if (result && result.policy) {
        setShowCreate(false);
        setNewVendorId("");
        fetchPolicies();
      } else {
        alert("Failed: " + (result.detail || "unknown"));
      }
    } catch (err) {
      alert("Failed: " + err.message);
    }
  };

  const handleUpdate = async (policyId, e) => {
    e.preventDefault();
    try {
      const fd = new FormData(e.target);
      const changes = {};
      for (const [key, value] of fd.entries()) {
        if (value) {
          if (key === "release_before_payment") {
            changes[key] = value === "true";
          } else {
            changes[key] = Number(value);
          }
        }
      }
      await updateAdminDemoPolicy(policyId, changes, token);
      setEditPolicy(null);
      fetchPolicies();
    } catch (err) {
      alert("Update failed: " + err.message);
    }
  };

  if (!token) return null;

  return (
    <main>
    <div className="admin-demos">
      <h1>Demo Policies</h1>

      <div className="admin-demos-filters">
        <div className="filter-group">
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New Policy
          </button>
        </div>
      </div>

      {loading && <div className="spinner" />}
      {error && <div className="error-message">{error}</div>}

      {!loading && !error && (
        <table className="admin-demos-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Vendor</th>
              <th>Base Fee</th>
              <th>Covered Days</th>
              <th>Late Fee</th>
              <th>Late Cap</th>
              <th>Conversion Day</th>
              <th>Max Rackets</th>
              <th>Release Before Pay</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {policies.length === 0 && (
              <tr><td colSpan="10">No policies found</td></tr>
            )}
            {policies.map((p) => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td>{p.vendor_name}</td>
                <td>${(p.base_fee_cents / 100).toFixed(2)}</td>
                <td>{p.covered_days}</td>
                <td>${(p.late_fee_cents / 100).toFixed(2)}</td>
                <td>${(p.late_fee_cap_cents / 100).toFixed(2)}</td>
                <td>{p.conversion_day}</td>
                <td>{p.max_rackets_per_ticket}</td>
                <td>{p.release_before_payment ? "Yes" : "No"}</td>
                <td>
                  <button className="btn-small" onClick={() => setEditPolicy({ ...p })}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create form */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>New Demo Policy</h3>
            <form onSubmit={handleUpsert}>
              <label>
                Vendor ID:
                <input type="number" value={newVendorId} onChange={(e) => setNewVendorId(e.target.value)} required />
              </label>
              <div className="form-grid">
                <label>Base Fee (cents) <input name="base_fee_cents" defaultValue={2500} /></label>
                <label>Base Fee Tax (cents) <input name="base_fee_tax_cents" defaultValue={0} /></label>
                <label>Covered Days <input name="covered_days" defaultValue={7} /></label>
                <label>Late Fee (cents) <input name="late_fee_cents" defaultValue={500} /></label>
                <label>Late Cap (cents) <input name="late_fee_cap_cents" defaultValue={10000} /></label>
                <label>Conversion Day <input name="conversion_day" defaultValue={30} /></label>
                <label>Max Rackets <input name="max_rackets_per_ticket" defaultValue={3} /></label>
                <label>Release Before Payment
                  <select name="release_before_payment" defaultValue="false">
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </label>
                <label>Sales Tax (bps) <input name="sales_tax_bps" placeholder="e.g. 1000 for 10%" /></label>
              </div>
              <div className="modal-actions">
                <button type="submit">Create</button>
                <button type="button" onClick={() => setShowCreate(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit form */}
      {editPolicy && (
        <div className="modal-overlay" onClick={() => setEditPolicy(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Policy #{editPolicy.id}</h3>
            <form onSubmit={(e) => handleUpdate(editPolicy.id, e)}>
              <div className="form-grid">
                <label>Base Fee (cents) <input name="base_fee_cents" defaultValue={editPolicy.base_fee_cents} /></label>
                <label>Base Fee Tax (cents) <input name="base_fee_tax_cents" defaultValue={editPolicy.base_fee_tax_cents} /></label>
                <label>Covered Days <input name="covered_days" defaultValue={editPolicy.covered_days} /></label>
                <label>Late Fee (cents) <input name="late_fee_cents" defaultValue={editPolicy.late_fee_cents} /></label>
                <label>Late Cap (cents) <input name="late_fee_cap_cents" defaultValue={editPolicy.late_fee_cap_cents} /></label>
                <label>Conversion Day <input name="conversion_day" defaultValue={editPolicy.conversion_day} /></label>
                <label>Max Rackets <input name="max_rackets_per_ticket" defaultValue={editPolicy.max_rackets_per_ticket} /></label>
                <label>Release Before Payment
                  <select name="release_before_payment" defaultValue={editPolicy.release_before_payment ? "true" : "false"}>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </label>
                <label>Sales Tax (bps) <input name="sales_tax_bps" defaultValue={editPolicy.sales_tax_bps || ""} /></label>
              </div>
              <div className="modal-actions">
                <button type="submit">Save</button>
                <button type="button" onClick={() => setEditPolicy(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </main>
  );
}
